import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";
import moment from "moment";
import {
  map,
  Observable,
  of,
  startWith,
  Subject,
  Subscription,
  takeUntil,
} from "rxjs";

import {
  DateTimeFormatter,
  getTimesList,
  isEndInRange,
  isOverlapping,
  isStartInRange,
} from "shared-utils";
import {
  ConferenceLinkType,
  IUser,
  IUserInfo,
  IUserViewInfo,
  RecurringActionType,
  TimeBlocker,
  TimeFormat,
} from "types";

import { DropdownWithSearchUsersComponent } from "@db-ui/dropdown-with-search-users";
// import { TextInputComponent } from "@db-ui/text-input";
import { TimePickerControlComponent } from "@db-ui/time-picker-control";
import { TooltipModule } from "primeng/tooltip";
import {
  IKioskScheduleEntry,
  IMeetingRoomKioskInfo,
} from "../../models/kiosk.model";
import {
  meetingRoomKioskBookingForm,
  MeetingRoomKioskBookingForm,
} from "./meeting-room-kiosk-booking-form";
import { FormatDateTimePipe } from "projects/shared/src/lib/pipes/format-date-time.pipe";
import { InputTextModule } from "primeng/inputtext";

const hoursList = getTimesList(24, 5);

type IPresetOption = { value: string | number; label: string };

@Component({
  standalone: true,
  selector: "db-meeting-room-kiosk-booking-form",
  templateUrl: "./meeting-room-kiosk-booking-form.component.html",
  styleUrls: ["./meeting-room-kiosk-booking-form.component.scss"],
  imports: [
    CommonModule,
    TimePickerControlComponent,
    ReactiveFormsModule,
    FormsModule,
    TooltipModule,
    FormatDateTimePipe,
    InputTextModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: MeetingRoomKioskBookingFormComponent,
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingRoomKioskBookingFormComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @ViewChild("inviteesDropdown")
  dropdownWithSearchUsersComponent!: DropdownWithSearchUsersComponent;

  readonly copyConferenceLinkTooltip = $localize`:@@meeting-room-booking-details-form|copy-link:Copy link`;
  readonly deleteConferenceLinkTooltip = $localize`:@@meeting-room-booking-details-form|delete-link:Delete link`;
  readonly invalidConferenceLinkUrlMessage = $localize`:@@meeting-room-booking-details-form|invalid-link:Invalid URL`;

  readonly conferenceListErrorListDictionary = [
    {
      name: "pattern",
      getLabel: () => this.invalidConferenceLinkUrlMessage,
    },
  ];

  private readonly destroy$ = new Subject<void>();
  private readonly dateTimeFormatter = inject(DateTimeFormatter);
  private onChange = (_: MeetingRoomKioskBookingForm["value"]) => {};
  private onTouch = (_: MeetingRoomKioskBookingForm["value"]) => {};
  readonly form = meetingRoomKioskBookingForm();

  // TODO
  readonly userViewInfoList$: Observable<IUserViewInfo[]> = of([]);
  // readonly userViewInfoList$: Observable<IUserViewInfo[]> = inject(BookingModel).selectors.users$.pipe(
  //   map((users) =>
  //     (users || [])
  //       .filter((user) => user.status === UserStatus.Active)
  //       .map(({ id, firstName, lastName, email, avatarColor, profileImage }) => ({
  //         id,
  //         firstName,
  //         lastName,
  //         email,
  //         avatarColor,
  //         profileImage,
  //       }))
  //   ),
  //   shareReplay(1)
  // );

  // readonly selectedUserId$$ = new BehaviorSubject<string | null>(null);

  TimeFormat = TimeFormat;
  ConferenceLinkType = ConferenceLinkType;
  RecurringActionType = RecurringActionType;

  @Input() timeBlockers: TimeBlocker[] | null = null;
  @Input() selectedUser: IUserInfo | null = null;
  @Input() timeFormat: TimeFormat | null = null;
  @Input() meetingRoom!: IMeetingRoomKioskInfo;
  @Input() selectedDayTimestamp: number | null = null;
  @Input() slot!: IKioskScheduleEntry;
  @Output() meetingTimeRangeInputChange = new EventEmitter<
    Pick<
      MeetingRoomKioskBookingForm["value"],
      "startTime" | "endTime" | "dateTimestamp"
    >
  >();
  @Output() selectedUserChange = new EventEmitter<IUser | null>();

  assignedUser: IUser | null = null;

  showRequestCateringAndServiceCategoriesDialog = false;

  firstAvailableTime = "";

  presetTitleOptions: IPresetOption[] = [
    { label: "Catch-up", value: "Catch-up" },
    { label: "Quick chat", value: "Quick chat" },
    { label: "Meeting", value: "Meeting" },
    { label: "Review", value: "Review" },
  ];
  presetTimeOptions: IPresetOption[] = [
    { label: "10 mins", value: 10 },
    { label: "15 mins", value: 15 },
    { label: "30 mins", value: 30 },
    { label: "45 mins", value: 45 },
    { label: "1 hour", value: 60 },
  ];
  smallestPresetTime = this.presetTimeOptions[0].value as number;

  hasSelectedPresetTime = false;

  private isInitialStartTimeSet = false;
  private isInitialEndTimeSet = false;
  private isInvitingOthersInitialized = false;
  private invitingOthersDropdownShowSubscription$$: Subscription | null = null;

  get timeFormatNumber(): 24 | 12 {
    return this.timeFormat === TimeFormat.TWELVE_HOUR ? 12 : 24;
  }

  readonly startTimeDisabledTimes$ = this.form
    .get("endTime")!
    .valueChanges.pipe(
      startWith(this.form.get("endTime")!.value),
      map(() => {
        const endValue = this.form.get("endTime")!.value;

        return hoursList
          .map(({ value }) => {
            // Disable times in the past
            // if (value < now) {
            //   return { value, disabled: true };
            // }
            // Disable times after end time
            if (value > endValue!) {
              return { value, disabled: true };
            }
            // Disable times that would create a zero-duration booking
            if (value === endValue) {
              return { value, disabled: true };
            }
            if (!this.timeBlockers || this.timeBlockers.length === 0) {
              return { value, disabled: false };
            }
            if (isStartInRange(value, this.timeBlockers)) {
              return { value, disabled: true };
            }
            if (isOverlapping([value, endValue!], this.timeBlockers)) {
              return { value, disabled: true };
            }
            return { value, disabled: false };
          })
          .filter(({ disabled }) => disabled)
          .map(({ value }) => value);
      }),
    );

  readonly endTimeDisabledTimes$ = this.form
    .get("startTime")!
    .valueChanges.pipe(
      startWith(this.form.get("startTime")!.value),
      map(() => {
        const startValue = this.form.get("startTime")!.value;
        // const now = moment().tz(this.meetingRoom.timezone).format("HH:mm");

        return hoursList
          .map(({ value }) => {
            // Disable times in the past
            // if (value < now) {
            //   return { value, disabled: true };
            // }
            // Disable times before start time
            if (value < startValue!) {
              return { value, disabled: true };
            }
            // Disable same time as start time (would create a zero-duration booking)
            if (value === startValue) {
              return { value, disabled: true };
            }
            if (!this.timeBlockers || this.timeBlockers.length === 0) {
              return { value, disabled: false };
            }
            if (isEndInRange(value, this.timeBlockers)) {
              return { value, disabled: true };
            }
            if (isOverlapping([startValue!, value], this.timeBlockers)) {
              return { value, disabled: true };
            }
            return { value, disabled: false };
          })
          .filter(({ disabled }) => disabled)
          .map(({ value }) => value);
      }),
    );

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      const { startTime, endTime, dateTimestamp } = value;
      this.meetingTimeRangeInputChange.emit({
        startTime,
        endTime,
        dateTimestamp,
      });
      this.onChange(value);

      if (!this.firstAvailableTime && !!startTime) {
        this.firstAvailableTime = startTime;
      }
    });

    this.form.get("dateTimestamp")?.patchValue(this.slot.startMoment.valueOf());

    const now = moment();
    const slotStartMoment = moment(this.slot.startTime);
    const startMoment = slotStartMoment.isBefore(now) ? now : slotStartMoment;

    const minuteDiff =
      startMoment.diff(moment(this.slot.endTime), "minutes") * -1;
    this.presetTimeOptions =
      minuteDiff < this.smallestPresetTime
        ? [this.presetTimeOptions[0]]
        : this.presetTimeOptions.filter(
            ({ value }) => (value as number) <= minuteDiff,
          );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.invitingOthersDropdownShowSubscription$$) {
      this.invitingOthersDropdownShowSubscription$$.unsubscribe();
    }
  }

  get areTimesEqual(): boolean {
    return this.form.hasError("timeEqual");
  }

  selectedUserChangedHandler(userId: string, usersList: IUserViewInfo[]): void {
    const user = usersList.find(({ id }) => id === userId);
    this.assignedUser = user as IUser;
    this.selectedUserChange.emit(this.assignedUser);
  }

  writeValue(value: MeetingRoomKioskBookingForm["value"]) {
    this.form.patchValue(value);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnChange(fn: (_: any) => void) {
    this.onChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnTouched(fn: (_: any) => void) {
    this.onTouch = fn;
  }

  handleTimeSelection(type: "start" | "end"): void {
    if (type === "start" && !this.isInitialStartTimeSet) {
      this.isInitialStartTimeSet = true;
      return;
    }
    if (type === "end" && !this.isInitialEndTimeSet) {
      this.isInitialEndTimeSet = true;
      return;
    }
  }

  timeToMoment = (time: number): moment.Moment => {
    return moment(time).tz(this.meetingRoom.timezone);
  };

  formatDateTime = (time: number): string => {
    return this.dateTimeFormatter.formatTimestamp(
      moment(time).tz(this.meetingRoom.timezone).valueOf(),
      "ddd, D MMM YYYY",
    );
  };

  handlePresetNameSelection(presetOption: IPresetOption): void {
    this.form.controls.title.patchValue(presetOption.value as string);
  }
  handlePresetTimeSelection(presetOption: IPresetOption): void {
    this.hasSelectedPresetTime = true;
    const bookingSize = (presetOption.value as number) / 5;
    const startTime = this.form.get("startTime")!.value;
    const startTimeIndex = hoursList.findIndex(
      ({ value }) => value === startTime,
    );
    const endTimeIndex = startTimeIndex + bookingSize;
    const endTime = hoursList[endTimeIndex].value;
    this.form.get("endTime")?.patchValue(endTime);
  }
}
